import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CurrentUser, DataRoomFolder } from '../../shared/models';
import React from 'react';

interface DocumentPageActionButtonProps {
	currentUser: CurrentUser;
	folder: DataRoomFolder;
	canCopyDocuments: boolean;
	onDownload: () => void;
	onAddFolder: (folder: DataRoomFolder) => void;
	onAddFile: (folder: DataRoomFolder) => void;
	onShowAll: (show: boolean) => void;
	onShowDeleted: (show: boolean) => void;
	onCopyDocuments: (folder: DataRoomFolder) => void;
	onMoveMultipleDocuments: (folder: DataRoomFolder) => void;
}

type PageActionItem = keyof typeof PageActionItemMap;
const PageActionItemMap = {
	Download: 'Download Folder',
	AddFolder: 'Add Folder',
	AddFiles: 'Add File(s)',
	ShowAll: 'Show All Documents',
	ShowDeleted: 'Show Deleted Documents',
	CopyDocuments: 'Copy Documents',
	MoveDocuments: 'Move Documents',
};

export const DocumentPageActionButton: React.FC<DocumentPageActionButtonProps> = ({
	currentUser,
	folder,
	canCopyDocuments,
	onDownload,
	onAddFolder,
	onAddFile,
	onShowAll,
	onShowDeleted,
	onCopyDocuments,
	onMoveMultipleDocuments,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isNotBanker = currentUser.isEmployeeUser || currentUser.isCompanyUser;

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: PageActionItem) => {
		switch (action) {
			case 'Download':
				onDownload();
				break;
			case 'AddFolder':
				onAddFolder(folder);
				break;
			case 'AddFiles':
				onAddFile(folder);
				break;
			case 'ShowAll':
				onShowAll(true);
				break;
			case 'ShowDeleted':
				onShowDeleted(true);
				break;
			case 'CopyDocuments':
				onCopyDocuments(folder);
				break;
			case 'MoveDocuments':
				onMoveMultipleDocuments(folder);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: PageActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Page Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Download')}>
										{PageActionItemMap['Download']}
									</MenuItem>
									{isNotBanker && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AddFolder')}>
											{PageActionItemMap['AddFolder']}
										</MenuItem>
									)}
									{isNotBanker && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'AddFiles')}>
											{PageActionItemMap['AddFiles']}
										</MenuItem>
									)}
									{currentUser.isEmployeeUser && canCopyDocuments && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'CopyDocuments')}>
											{PageActionItemMap['CopyDocuments']}
										</MenuItem>
									)}
									{currentUser.isEmployeeUser && folder.files.length > 0 && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'MoveDocuments')}>
											{PageActionItemMap['MoveDocuments']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'ShowAll')}>
										{PageActionItemMap['ShowAll']}
									</MenuItem>
									{currentUser.isEmployeeUser && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ShowDeleted')}>
											{PageActionItemMap['ShowDeleted']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
